import {
  Button,
  Col,
  FieldLabel,
  Form,
  type OperationType,
  RemovableCard,
  Row,
  SelectField,
} from '@/components';
import { useProjectFormContext } from '@/features/project/_hooks/form-context';
import { useProjectOptions } from '@/features/project/_hooks/project';
import { sheetTypeEnum } from '@/utils/enum';
import { PlusOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { type UseFieldArrayRemove, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type Props = {
  operation: OperationType;
  type: z.infer<typeof sheetTypeEnum>;
  ignoredProjectIds?: string[];
};
export const RelatedProjectArrayField: FC<Props> = (props) => {
  const { control } = useProjectFormContext();

  const { t } = useTranslation();

  const {
    fields: relatedProjectFields,
    append: appendRelatedProject,
    remove: removeRelatedProject,
  } = useFieldArray({
    control,
    name: 'data.relatedProjects',
  });

  return (
    <Form.Item label={<FieldLabel text={t('related-projects')} />}>
      <Row gutter={[24, 8]}>
        {relatedProjectFields.map((item, idx) => {
          return (
            <Col key={item.id} span={24}>
              <RelatedProjectField
                {...props}
                idx={idx}
                removeRelatedProject={removeRelatedProject}
              />
            </Col>
          );
        })}
        <Col span={24}>
          <Button
            type="dashed"
            onClick={() => {
              appendRelatedProject({
                id: '',
                relationType: 'others-base',
              });
            }}
            block
            icon={<PlusOutlined />}
          >
            {t('add-related-project')}
          </Button>
        </Col>
      </Row>
    </Form.Item>
  );
};

const RelatedProjectField = ({
  idx,
  removeRelatedProject,
  ignoredProjectIds,
  operation,
  type,
}: {
  idx: number;
  removeRelatedProject: UseFieldArrayRemove;
} & Props) => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = useProjectFormContext();
  const [fiscalYear, relatedProject] = watch([
    'data.fiscalYear',
    `data.relatedProjects.${idx}`,
  ]);

  const requestSheetType = (() => {
    switch (relatedProject.relationType) {
      case 'ks-link-base':
      case 'ss-link-base':
        return sheetTypeEnum.Values.RS;
      case 'ks-link-target':
        return sheetTypeEnum.Values.KS;
      case 'ss-link-target':
        return sheetTypeEnum.Values.SS;
      default:
        return undefined;
    }
  })();

  const projectOptions = useProjectOptions({
    params: {
      fiscalYear: fiscalYear?.year().toString(),
      sheetType: requestSheetType,
      // RSシートタイプの事業では、関連事業としてレビュー対象外の事業を選択できないように制限
      // skipAdministrativeReformTeamEvaluation=falseで、レビュー対象の事業のみを取得
      skipAdministrativeReformTeamEvaluation: type === 'RS' ? false : undefined,
    },
    enabled: !!fiscalYear,
    select: (data) => {
      const filteredData =
        operation === 'edit' && ignoredProjectIds
          ? data.filter((option) => !ignoredProjectIds.includes(option.value))
          : data;
      return filteredData.sort((a, b) => a.label.localeCompare(b.label));
    },
  });
  return (
    <RemovableCard
      onRemove={() => {
        removeRelatedProject(idx);
      }}
    >
      <Row gutter={[24, 0]}>
        {/* <Col span={12}>
          <SelectField
            name={`data.relatedProjects.${idx}.relationType`}
            label={t('related-project-relation-type')}
            control={control}
            errors={errors}
            options={projectRelationType.options.map((value) => ({
              value,
              label: ProjectRelationTypeLabelModifier[value],
            }))}
            shouldCoexistOnChange
            onChange={() => {
              resetField(`data.relatedProjects.${idx}.id`);
            }}
            required
          />
        </Col> */}

        <Col span={24}>
          <SelectField
            name={`data.relatedProjects.${idx}.id`}
            label={t('project-name')}
            control={control}
            errors={errors}
            options={projectOptions.data}
            showSearch
            required
            loading={projectOptions.isLoading}
          />
        </Col>
      </Row>
    </RemovableCard>
  );
};
